import { getTimezone } from "@/services/Api";
import moment, { Moment } from "moment";
import { PaymentType, PrintSize } from "./Enums";
import { PriceLine, Slot } from "./Product";

export class Order {
  id = 0;
  priceType = 0;
  orderNumber = 0;
  currencyId = 0;
  channelName = "";
  priceTypeName = "";
  remarks = "";
  products = Array<OrderProduct>();
  businessClientId: number | null = null;
  businessClient: BusinessClient | null = null;
  customer = new Customer();
  payment = new OrderPayment();
  arrivalConfirmation = 0;
  createDate = "";
  status = 0;
  statusName = "";
  commissionPercent = 0;
  commissionTotal = 0;
  receiptTotal = 0;
  isReserved = false;
  showAllPriceLines = false;
  voucherCode = "";
  sourceOptionId: number | null = null;
  createdNow = false;
  receiptsJobId: number | null = null;
  receiptPrintSize = '';
  ticketPrintSize = '';
  skipSaveOrder = false;
}

export class Voucher {
  products = Array<OrderProduct>();
}
export class BusinessClient {
  id = 0;
  name = "";
  businessClientTypeId = 0;
  businessClientTypeName = "";
  photoInstructions: string[] = [];
  instruction = '';
  companyLogoUrl = "";
  isSubsidized = false;
  businessClientDiscountCombinations: businessClientDiscountCombination[] = [];
}

export class businessClientDiscountCombination {
  channelId = 0;
  currencyId = 0;
  discount = 0;
  priceTypeId = 0;
}

export class CardCode {
  code = "";
  amount = 0;
  couponCode = "";

  constructor(
    code = "",
    amount = 0,
    couponCode = ""
  ) {
    this.code = code;
    this.amount = amount;
    this.couponCode = couponCode;
  }
}

export class OrderProduct {
  orderProductId = 0;
  id = 0;
  date = "";
  priceLine: PriceLine = new PriceLine();
  slotId = 0;
  slotFakeId: string | null = null;
  slotStartTime = "";
  slotEndTime = "";
  slotDuration = "";
  name = "";
  channelId = 0;
  priceId = 0;
  startDateTime = "";
  defaultPriceForOne = 0;
  notValidCoupon = false;
  cardCode = "";

  constructor(
    id: number,
    name: string,
    channelId: number,
    priceId: number,
    defaultPriceForOne = 0,
    cardCode = ""
  ) {
    this.id = id;
    this.name = name;
    this.channelId = channelId;
    this.priceId = priceId;
    this.defaultPriceForOne = defaultPriceForOne;
    this.cardCode = cardCode
  }

  withPrice(priceLine: PriceLine): this {
    this.priceLine = priceLine;
    this.priceLine.quantity++;
    return this;
  }

  withSlot(slot: Slot): this {
    this.slotId = slot.id;
    this.slotEndTime = slot.endTime;
    this.slotStartTime = slot.startTime;
    this.slotDuration = slot.duration;
    this.slotFakeId = slot.slotFakeId;
    return this;
  }

  withDate(date: string): this {
    this.date = date;
    return this;
  }

  withDiscount(discount: number): this {
    this.priceLine.discount = (this.priceLine.priceForOne * this.priceLine.quantity) >= discount ? discount : (this.priceLine.priceForOne * this.priceLine.quantity);
    return this;
  }
}

export class Customer {
  firstName = "";
  lastName = "";
  mobile = "";
  email = "";
  remarks = "";
  sendTicket = false;
  groupName = "";
  pnr = "";
  sourceOptionId: number | null = null
}

export class OrderPayment {
  index = 0;
  orderId = 0;
  valueDate: string = moment(new Date()).format("DD/MM/YYYY");
  timeZone: number = getTimezone();
  cashPayments = Array<CashPayment>();
  checkPayments = Array<CheckPayment>();
  bankTransferPayments = Array<BankTransferPayment>();
  creditCardPayments = Array<CreditCardPayment>();
  get payments() {
    return new Array<BasePayment>(
      ...this.cashPayments,
      ...this.checkPayments,
      ...this.bankTransferPayments,
      ...this.creditCardPayments
    ).sort((a, b) => b.index - a.index);
  }

  addPayment(p: BasePayment) {
    p.index = this.index++;

    if (p.paymentType == PaymentType.Cash) {
      this.cashPayments.unshift(p);
    }

    if (p.paymentType == PaymentType.Check) {
      this.checkPayments.unshift(p as CheckPayment);
    }

    if (p.paymentType == PaymentType.BankTransfer) {
      this.bankTransferPayments.unshift(p as BankTransferPayment);
    }

    if (p.paymentType == PaymentType.CreditCard) {
      this.creditCardPayments.unshift(p as CreditCardPayment);
    }
  }

  deletePayment(idx: number) {
    const p = this.payments.find((x) => x.index == idx);

    if (!p) {
      return;
    }

    let founded = -1;

    if (p.paymentType == PaymentType.Cash) {
      founded = this.cashPayments.findIndex((x) => x.index == idx);
      this.cashPayments.splice(founded, 1);
    } else if (p.paymentType == PaymentType.Check) {
      founded = this.checkPayments.findIndex((x) => x.index == idx);
      this.checkPayments.splice(founded, 1);
    } else if (p.paymentType == PaymentType.BankTransfer) {
      founded = this.bankTransferPayments.findIndex((x) => x.index == idx);
      this.bankTransferPayments.splice(founded, 1);
    } else if (p.paymentType == PaymentType.CreditCard) {
      founded = this.creditCardPayments.findIndex((x) => x.index == idx);
      this.creditCardPayments.splice(founded, 1);
    }

    this.recalculateIdx();
  }

  recalculateIdx() {
    let idx = this.payments.length;
    for (const p of this.payments) {
      p.index = --idx;
    }
    this.index = this.payments.length;
  }
}

export class BasePayment {
  id = 0;
  index = 0;
  paymentType: PaymentType;
  amount = 0;
  currencyId = 0;
  localCurrencyRate = 1;
  documentNumber = 0;

  constructor(paymentType: PaymentType) {
    this.paymentType = paymentType;
  }
}

export class CashPayment extends BasePayment {
  constructor() {
    super(PaymentType.Cash);
  }
}

export class CheckPayment extends BasePayment {
  valueDate: string = moment(new Date()).format("DD/MM/YYYY");
  owner = "";
  accountNumber = "";
  bankId = 0;
  bankBranchNumber: number | null = null;
  checkNumber: number | null = null;

  constructor() {
    super(PaymentType.Check);
  }
}

export class BankTransferPayment extends BasePayment {
  valueDate: string = moment(new Date()).format("DD/MM/YYYY");
  accountNumber = "";
  receivingAccount = "";
  bankId = 0;
  bankBranchNumber: number | null = null;
  referenceNumber = 0;

  constructor() {
    super(PaymentType.BankTransfer);
  }
}

export class CreditCardPayment extends BasePayment {
  transactionId = 0;
  creditCardPaymentType = 1;
  numberOfPayments = 1;

  constructor() {
    super(PaymentType.CreditCard);
  }
}

export class ReservedOrder {
  showDetails = false;
  id = 0;
  orderNumber = "";
  createDate = "";
  startDate = "";
  leadCustomerFullName = "";
  leadCustomerMobile = "";
  channelName = "";
  channelId = 0;
  status = 0;
  businessOrder = false;
  productName = "";
}

export class NotAvailableProducts {
  productId = 0;
  startTime = "";
  isAvailable = false;
  notAvailableCustomerTypeIds: Array<number> = [];
}

export class OrderFilter {
  searchValue = "";
  searchType = 1;
  keywords = "";
  orderNumber = "";
  customerName = "";
  pnr = "";
  mobile = "";
  groupName = "";
  businessClientId: number | null = null;
  pageIndex = 0;
  advancedSearch = false;
  reservedOrderLastPosition: number | null = null;
  arrivalDateFrom: Date | null = null;
  arrivalDateTo: Date | null = null;
  productId: number | null = null;
}

export class Budget {
  resultMessage = "";
  updatedBudget = 0;
  companyId = 0;
  employeeName = "";
  clientName = "";
  benefitList = Array<Benefit>();
  memberId = "";
  lastName = "";
  firstName = "";
  organizationName = "";
  organizationId = "";
  cardCode = "";
}

export class Benefit {
  benefitName = "";
  amount = 0;
  couponCodes = Array<string>();
  benefitId = 0;
  expirationDateAvailable = false;
  expirationDate = "";
  organizationId = "";
}

export class CouponAvailability {
  couponId = 0;
  couponName = "";
  couponCode = "";

  productId = 0;
  productName = "";

  customerTypeId = 0;
  customerTypeName = "";
  ticketLimitation: null | number = null;
  qty = 0;
  discountAmount = null;
  discountPercent = null;
  expirationDate = null;
  date: string | null = null;
  slot: Slot | null = null;
  priceLine
}

export class CouponPriceLine {
  id = 0;
  name = "";
  priceForOne = 0;
  quantity = 0;
  discount = 0;
}